const LOAD_ERROR =
    'No pudimos cargar el formulario de pago. Inténtalo de nuevo.';
const BOOTSTRAP_KEY = Symbol.for('rickymau.stripeCheckoutBootstrap');

function createStripeCheckoutBootstrap({
    documentTarget = document,
    importCheckout = () => import('./stripe-checkout'),
    windowTarget = window,
} = {}) {
    let activeRoot = null;
    let checkoutModule = null;
    let generation = 0;
    let pendingMount = null;
    let retry = null;

    function clearRetry() {
        if (!retry) {
            return;
        }

        retry.button.removeEventListener('click', retry.handler);
        retry = null;
    }

    function clearLoadError() {
        const messageElement = documentTarget.getElementById('payment-message');
        const textElement = messageElement?.querySelector(
            '[data-payment-message-text]',
        );
        const retryButton = messageElement?.querySelector(
            '[data-stripe-retry]',
        );

        if (textElement) {
            textElement.textContent = '';
        }

        if (retryButton) {
            retryButton.hidden = true;
        }

        messageElement?.classList.add('hidden');
        clearRetry();
    }

    function showLoadError(root) {
        const messageElement = documentTarget.getElementById('payment-message');
        const textElement = messageElement?.querySelector(
            '[data-payment-message-text]',
        );
        const retryButton = messageElement?.querySelector(
            '[data-stripe-retry]',
        );
        const payButton = documentTarget.getElementById('stripe-pay-btn');

        root.dataset.stripeState = 'idle';
        if (payButton) {
            payButton.disabled = true;
            payButton.setAttribute?.('aria-disabled', 'true');
        }

        if (!messageElement || !textElement || !retryButton) {
            return;
        }

        textElement.textContent = LOAD_ERROR;
        retryButton.hidden = false;
        messageElement.classList.remove('hidden');
        clearRetry();

        const handler = async (event) => {
            event.preventDefault();
            clearLoadError();
            await mount();
        };
        retry = { button: retryButton, handler };
        retryButton.addEventListener('click', handler);
    }

    function destroy() {
        generation += 1;
        pendingMount = null;
        clearRetry();

        if (activeRoot) {
            checkoutModule?.destroyStripeCheckout(activeRoot);
        }

        activeRoot = null;
    }

    function dispose() {
        destroy();
        checkoutModule?.disposeStripeCheckout?.();
        checkoutModule = null;
    }

    function isCurrentMount(mountState) {
        return (
            pendingMount === mountState &&
            mountState.generation === generation &&
            activeRoot === mountState.root
        );
    }

    async function reconcileMount(mountState) {
        const { root } = mountState;

        try {
            const resolvedModule = checkoutModule ?? (await importCheckout());
            if (!isCurrentMount(mountState)) {
                return;
            }

            if (
                typeof resolvedModule.initStripeCheckout !== 'function' ||
                typeof resolvedModule.destroyStripeCheckout !== 'function'
            ) {
                throw new TypeError('Invalid Stripe checkout module.');
            }

            checkoutModule = resolvedModule;

            while (true) {
                clearLoadError();
                await resolvedModule.initStripeCheckout(root);

                if (!isCurrentMount(mountState)) {
                    return;
                }

                if (!mountState.dirty) {
                    pendingMount = null;

                    return;
                }

                mountState.dirty = false;
            }
        } catch {
            if (isCurrentMount(mountState)) {
                checkoutModule = null;
                showLoadError(root);
                pendingMount = null;
            }
        }
    }

    async function mount() {
        const root = documentTarget.getElementById('checkout-stripe-root');
        if (!root || !windowTarget.Livewire) {
            return;
        }

        if (pendingMount?.root === root) {
            const currentMount = pendingMount;
            currentMount.dirty = true;
            await currentMount.promise;

            return;
        }

        if (activeRoot && activeRoot !== root) {
            destroy();
        }

        activeRoot = root;
        root.dataset.stripeState = 'initializing';
        const payButton = documentTarget.getElementById('stripe-pay-btn');
        if (payButton) {
            payButton.disabled = true;
            payButton.setAttribute?.('aria-disabled', 'true');
        }

        const mountState = {
            dirty: false,
            generation,
            promise: null,
            root,
        };
        pendingMount = mountState;
        const operation = reconcileMount(mountState);
        mountState.promise = operation;

        try {
            await operation;
        } finally {
            if (pendingMount === mountState) {
                pendingMount = null;
            }
        }
    }

    return { destroy, dispose, mount };
}

function installStripeCheckoutBootstrap({
    documentTarget = document,
    importCheckout = () => import('./stripe-checkout'),
    windowTarget = window,
} = {}) {
    windowTarget[BOOTSTRAP_KEY]?.dispose();

    const bootstrap = createStripeCheckoutBootstrap({
        documentTarget,
        importCheckout,
        windowTarget,
    });
    let disposed = false;
    let livewireHookCleanup = null;
    let livewireHookOwner = null;

    function attachLivewireHook() {
        const livewire = windowTarget.Livewire;
        if (!livewire || livewireHookOwner === livewire) {
            return;
        }

        livewireHookCleanup?.();
        livewireHookCleanup = null;
        livewireHookOwner = livewire;
        if (typeof livewire.hook === 'function') {
            livewireHookCleanup = livewire.hook('morphed', () => mount());
        }
    }

    async function mount() {
        if (disposed) {
            return;
        }

        attachLivewireHook();
        await bootstrap.mount();
    }

    function destroy() {
        bootstrap.destroy();
    }

    const onMount = () => mount();
    const onNavigate = () => destroy();

    documentTarget.addEventListener('DOMContentLoaded', onMount);
    documentTarget.addEventListener('livewire:initialized', onMount);
    documentTarget.addEventListener('livewire:navigated', onMount);
    documentTarget.addEventListener('livewire:navigating', onNavigate);

    const installation = {
        destroy,
        dispose() {
            if (disposed) {
                return;
            }

            disposed = true;
            documentTarget.removeEventListener('DOMContentLoaded', onMount);
            documentTarget.removeEventListener('livewire:initialized', onMount);
            documentTarget.removeEventListener('livewire:navigated', onMount);
            documentTarget.removeEventListener(
                'livewire:navigating',
                onNavigate,
            );
            livewireHookCleanup?.();
            livewireHookCleanup = null;
            livewireHookOwner = null;
            bootstrap.dispose();

            if (windowTarget[BOOTSTRAP_KEY] === installation) {
                delete windowTarget[BOOTSTRAP_KEY];
            }
        },
        mount,
    };
    windowTarget[BOOTSTRAP_KEY] = installation;

    return installation;
}

export { createStripeCheckoutBootstrap, installStripeCheckoutBootstrap };
